import React, { type JSX, useEffect, useRef } from 'react';

import type { ZoningDetailsData } from './zoneTypes';

interface ZoningTableProps {
  apn: string;
  zoningForParcel: ZoningDetailsData;
}

type ZoneomicsObjectDataTypes = string | string[] | boolean | undefined;

const ZoningTable = ({ apn, zoningForParcel }: ZoningTableProps) => {
  const tableStyle = {
    width: '100%',
    borderCollapse: 'collapse' as 'collapse',
  };

  const cellStyle = {
    border: '1px solid #ccc',
    padding: '8px',
  };

  const headerStyle = {
    ...cellStyle,
    fontWeight: 'bold',
    textTransform: 'capitalize' as 'capitalize',
    backgroundColor: '#f2f2f2',
  };

  const rowStyle = {
    backgroundColor: 'white',
  };

  // Helper function to convert snake_case to readable format
  const convertSnakeCaseToReadable = (str: string) => {
    return str
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const formatControlDataToJSX = (data: { [key: string]: Record<string, string> }): JSX.Element => {
    return (
      <table>
        <tbody>
          {Object.keys(data).map((key) => (
            <tr key={key}>
              <td className="w-[300px]">{convertSnakeCaseToReadable(key)}</td>
              <td>{data[key].toString().replace('STF', 'See text field')}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  const renderValue = (value: ZoneomicsObjectDataTypes) => {
    if (typeof value === 'string' && value.startsWith('https')) {
      return (
        <a href={value} target="_blank" rel="noopener noreferrer" className="text-blue-50 underline hover:text-blue-60">
          {value}
        </a>
      );
    } else if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    } else if (Array.isArray(value)) {
      return value.join(', ');
    } else if (value === 'STF') {
      return 'See text field';
    } else if (
      value !== null &&
      typeof value === 'object' &&
      (Object.keys(value).includes('standard') || Object.keys(value).includes('non-standard'))
    ) {
      return (
        <div>
          <strong>Standard:</strong> {formatControlDataToJSX(value['standard'] || {})}
          <br />
          <strong>Non-Standard:</strong>
          {formatControlDataToJSX(value['non-standard'] || {})}
        </div>
      );
    } else {
      return value?.toString();
    }
  };

  const tableRef = useRef<HTMLTableElement>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  useEffect(() => {
    const handleExportCsv = () => {
      handleExportClick();
    };

    if (tableRef?.current) {
      tableRef.current.addEventListener('exportCsv', handleExportCsv);

      return () => {
        tableRef.current?.removeEventListener('exportCsv', handleExportCsv);
      };
    }
  }, [tableRef]);

  type AnyObject = { [key: string]: any };

  const flattenObject = (obj: AnyObject, parent: string = '', res: AnyObject = {}): AnyObject => {
    for (const key in obj) {
      if (Object.hasOwn(obj, key)) {
        const propName = parent ? `${parent} - ${key}` : key;
        if (typeof obj[key] === 'object' && obj[key] !== null && !Array.isArray(obj[key])) {
          flattenObject(obj[key], propName, res);
        } else {
          res[propName] = obj[key];
        }
      }
    }
    return res;
  };

  const handleExportClick = () => {
    let csvContent = '';
    Object.entries(zoningForParcel)
      .filter(([entry]) => !['lat', 'lng'].includes(entry.toString()))
      .forEach(([mainKey, mainValue]) => {
        csvContent += `${convertSnakeCaseToReadable(mainKey)}\n`;

        const flatObject = typeof mainValue === 'object' ? flattenObject(mainValue) : { mainKey: mainValue };

        Object.entries(flatObject).forEach(([key, value]) => {
          const header = convertSnakeCaseToReadable(key);
          const row = Array.isArray(value) ? value.join(', ') : value?.toString();

          csvContent += `"${header}","${row}"\n`;
        });

        csvContent += '\n';
      });

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `zoning_data_${apn}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <table ref={tableRef} id={'zoning-table'} style={tableStyle}>
      <tbody>
        {zoningForParcel ? (
          Object.entries(zoningForParcel)
            .filter(([mainKey, mainValue]) => mainValue !== null && !['lat', 'lng'].includes(mainKey))
            .map(([mainKey, mainValue]) => (
              <React.Fragment key={mainKey}>
                <tr style={headerStyle}>
                  <th colSpan={2}>{convertSnakeCaseToReadable(mainKey)}</th>
                </tr>
                {Object.entries(mainValue).map(([key, value]) => (
                  <tr key={key} style={rowStyle}>
                    <td style={cellStyle}>
                      <strong>{convertSnakeCaseToReadable(key)}</strong>
                    </td>
                    <td style={cellStyle}>{renderValue(value as ZoneomicsObjectDataTypes)}</td>
                  </tr>
                ))}
              </React.Fragment>
            ))
        ) : (
          <tr>
            <td>Error getting zoning data</td>
          </tr>
        )}
      </tbody>
    </table>
  );
};

export default ZoningTable;
